import { TIME_CONTROLS } from '../chess/game.js';
import { ratingBucketForCertificate } from './leaderboardBuckets.js';

const GLICKO_SCALE = 173.7178;
const DEFAULT_TAU = 0.5;
const EPSILON = 0.000001;

export const DEFAULT_RATING = Object.freeze({
  rating: 1500,
  deviation: 350,
  volatility: 0.06,
  games: 0
});

export function timeControlClass(timeControl) {
  const control = TIME_CONTROLS[timeControl];
  if (!control) {
    throw new Error(`Unknown time control: ${timeControl}`);
  }
  return control.class;
}

function g(phi) {
  return 1 / Math.sqrt(1 + (3 * phi ** 2) / Math.PI ** 2);
}

function expectation(mu, opponentMu, opponentPhi) {
  return 1 / (1 + Math.exp(-g(opponentPhi) * (mu - opponentMu)));
}

function volatilityFunction(x, delta, phi, variance, alpha, tau) {
  const exponential = Math.exp(x);
  const numerator = exponential * (delta ** 2 - phi ** 2 - variance - exponential);
  const denominator = 2 * (phi ** 2 + variance + exponential) ** 2;
  return numerator / denominator - (x - alpha) / tau ** 2;
}

function nextVolatility(phi, volatility, delta, variance, tau) {
  const alpha = Math.log(volatility ** 2);
  let lower = alpha;
  let upper;

  if (delta ** 2 > phi ** 2 + variance) {
    upper = Math.log(delta ** 2 - phi ** 2 - variance);
  } else {
    let multiplier = 1;
    while (
      volatilityFunction(
        alpha - multiplier * tau,
        delta,
        phi,
        variance,
        alpha,
        tau
      ) < 0
    ) {
      multiplier += 1;
    }
    upper = alpha - multiplier * tau;
  }

  let lowerValue = volatilityFunction(
    lower,
    delta,
    phi,
    variance,
    alpha,
    tau
  );
  let upperValue = volatilityFunction(
    upper,
    delta,
    phi,
    variance,
    alpha,
    tau
  );

  while (Math.abs(upper - lower) > EPSILON) {
    const candidate =
      lower + ((lower - upper) * lowerValue) / (upperValue - lowerValue);
    const candidateValue = volatilityFunction(
      candidate,
      delta,
      phi,
      variance,
      alpha,
      tau
    );
    if (candidateValue * upperValue <= 0) {
      lower = upper;
      lowerValue = upperValue;
    } else {
      lowerValue /= 2;
    }
    upper = candidate;
    upperValue = candidateValue;
  }

  return Math.exp(lower / 2);
}

export function updateGlicko2(
  player = DEFAULT_RATING,
  results = [],
  { tau = DEFAULT_TAU } = {}
) {
  const mu = (player.rating - 1500) / GLICKO_SCALE;
  const phi = player.deviation / GLICKO_SCALE;

  if (results.length === 0) {
    return {
      ...player,
      deviation: GLICKO_SCALE * Math.sqrt(phi ** 2 + player.volatility ** 2)
    };
  }

  const normalized = results.map(({ opponent, score }) => ({
    opponentMu: (opponent.rating - 1500) / GLICKO_SCALE,
    opponentPhi: opponent.deviation / GLICKO_SCALE,
    score
  }));
  const variance =
    1 /
    normalized.reduce((sum, result) => {
      const expected = expectation(mu, result.opponentMu, result.opponentPhi);
      return (
        sum
        + g(result.opponentPhi) ** 2 * expected * (1 - expected)
      );
    }, 0);
  const improvement =
    variance
    * normalized.reduce((sum, result) => {
      const expected = expectation(mu, result.opponentMu, result.opponentPhi);
      return sum + g(result.opponentPhi) * (result.score - expected);
    }, 0);
  const volatility = nextVolatility(
    phi,
    player.volatility,
    improvement,
    variance,
    tau
  );
  const preDeviation = Math.sqrt(phi ** 2 + volatility ** 2);
  const nextDeviation =
    1 / Math.sqrt(1 / preDeviation ** 2 + 1 / variance);
  const nextMu =
    mu
    + nextDeviation ** 2
      * normalized.reduce((sum, result) => {
        const expected = expectation(mu, result.opponentMu, result.opponentPhi);
        return sum + g(result.opponentPhi) * (result.score - expected);
      }, 0);

  return {
    rating: 1500 + GLICKO_SCALE * nextMu,
    deviation: GLICKO_SCALE * nextDeviation,
    volatility,
    games: (player.games || 0) + results.length
  };
}

function scorePair(result) {
  if (result === '1-0') {
    return [1, 0];
  }
  if (result === '0-1') {
    return [0, 1];
  }
  return [0.5, 0.5];
}

function playerRatings(book, playerId) {
  if (!book[playerId]) {
    book[playerId] = {};
  }
  return book[playerId];
}

function ensureRatingBucket(book, playerId, bucket) {
  const ratings = playerRatings(book, playerId);
  if (!ratings[bucket]) {
    ratings[bucket] = { ...DEFAULT_RATING };
  }
  return ratings[bucket];
}

export function replayCertificateRatings(certificates) {
  const ratings = {};
  const ordered = [...certificates]
    .filter((certificate) => certificate.rated)
    .sort(
      (left, right) =>
        left.endTs - right.endTs
        || String(left.certificateHash).localeCompare(String(right.certificateHash))
    );

  for (const certificate of ordered) {
    const [white, black] = certificate.players;
    const ratingBucket = ratingBucketForCertificate(certificate);
    const whiteBefore = ensureRatingBucket(ratings, white.playerId, ratingBucket);
    const blackBefore = ensureRatingBucket(ratings, black.playerId, ratingBucket);
    const [whiteScore, blackScore] = scorePair(certificate.result);

    playerRatings(ratings, white.playerId)[ratingBucket] = updateGlicko2(whiteBefore, [
      { opponent: blackBefore, score: whiteScore }
    ]);
    playerRatings(ratings, black.playerId)[ratingBucket] = updateGlicko2(blackBefore, [
      { opponent: whiteBefore, score: blackScore }
    ]);
  }

  return ratings;
}

export function displayRating(rating) {
  return Math.round(rating?.rating ?? DEFAULT_RATING.rating);
}
